import { addJobToPurchaseOrder } from '../../fabrication-purchase-orders/ui/add-job-to-purchase-order';
import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import {
  canJobJoinPurchaseOrder,
  getAvailableJobTransitions,
  transitionAffectsQuoteItems
} from '../data/fabrication-job-transitions';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement, state } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryTabManagerView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { type DeliveryCandidate, type DeliveryEntry, RecordDeliveryDialog } from './record-delivery-dialog';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItemFrameView } from './fabrication-job-item-frame-view';
import { FabricationJobItemsTable, JobItemRow } from './fabrication-job-items-table';
import { FabricationJobState } from '../../api/fabrication-job-api';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import {
  getAddToPurchaseOrderBadge,
  getPurchaseOrderBadgeLink
} from '../../dealer-franchisee/fabrication-purchase-orders/data/fabrication-purchase-order-helper-functions';
import { getApiFactory } from '../../api/api-injector';
import { getBranchUserCache } from '../../dealer-franchisee/cache-impl/dealer-branch-user-cache';
import { getFabricationJobStateLabel, getFabricationJobStatus } from '../data/fabrication-job-helper-functions';
import { getQuoteContainerManager } from '../../dealer-franchisee/quotes/quote-ui-adapter';
import { html, TemplateResult } from 'lit';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LangFabricationJobDetailView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-detail-view-lang';
import { LangFabricationPurchaseOrder } from '../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { NoPurchaseOrderDialog } from './no-purchase-order-dialog';
import { nowServer } from '../../../webmodule-common/other/datetime-converter';
import { offerAdvanceToAwaitingOrder } from '../ui/offer-advance-to-awaiting-order';
import { OptimizationPanel } from './optimization-panel';
import { PurchaseOrderBadgeView } from '../../api/fabrication-purchase-order-api';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { QuoteItemView, QuoteItemViewOptions } from '../../quotes/views/quote-item-view';
import { Snippet } from '../../../webmodule-common/other/ui/events';
import { supplierQuoteItemContentType } from '../../quotes/data/supplier-quote-item-content-type';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import { TenantLoginPublicInfo } from '../../api/dealer-api-interface-user';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import type { TabAction, TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';

export interface FabricationJobViewOptions {
  containerManager: FabricationJobContainerManager;
  onDelete?: () => Promise<void>;
}

@customElement('wm-fabricationjobdataentryview')
export class FabricationJobDataEntryView extends DataEntryTabManagerView {
  private containerManager: FabricationJobContainerManager;
  private dataBinding: DataBinding;
  private dataTracker: DataTracker;
  private onDelete?: () => Promise<void>;
  private quoteManagerCache = new Map<string, QuoteContainerManager>();
  @state()
  private itemsTable?: FabricationJobItemsTable;
  @state()
  private optimizationPanel?: OptimizationPanel;
  @state()
  private poBadges: PurchaseOrderBadgeView[] = [];
  private readonly branchUserCache = getBranchUserCache();

  constructor(options: FabricationJobViewOptions, owner: DataEntryOwner) {
    super(owner);
    this.containerManager = options.containerManager;
    this.onDelete = options.onDelete;

    this.dataBinding = new DataBinding(this.ui, undefined, input => `fabrication-job-${input}`);
    this.dataTracker = new DataTracker(this.dataBinding);

    const cm = this.containerManager;
    const addField = (fieldName: string, type: FieldType = FieldType.string) => {
      this.dataTracker.addObjectBinding(() => cm.fabricationJob, fieldName, fieldName, type, false);
    };

    addField('title');
    addField('description');
    addField('dateCreated', FieldType.dateTime);
    addField('lastModifiedDate', FieldType.dateTime);
    addField('assignedToUserId');
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.quoteManagerCache.clear();
  }

  public override async afterConstruction(): Promise<void> {
    // super creates the tab manager, which owns the footer actions.
    await super.afterConstruction();
    this.buildActionMenu();
    await Promise.all([this.loadPoBadges(), this.branchUserCache.prefetch()]);
    const job = this.containerManager.fabricationJob;
    await this.branchUserCache.addUsersToBranch(job.branchId, [job.assignedToUserId]);
  }

  protected override bodyTemplate(): TemplateResult {
    // Rebuilt on every render so the Status menu tracks the job's current state and lock: the
    // legal next states change the moment a transition lands, and a lock change arrives only as a
    // plain re-render - there is no lock-change callback on the container manager to hook instead.
    // Why rebuild in render rather than at the few call sites that change state? Because quote-view
    // establishes that pattern for this view family - it rebuilds its action menu in bodyTemplate
    // the same way. Moving it to explicit call sites would silently drop out-of-band lock changes,
    // so this is deliberate, not a side effect to factor out. (buildActionMenu guards itself against
    // a re-render mid-teardown - see below.)
    this.buildActionMenu();
    return super.bodyTemplate() as TemplateResult;
  }

  /**
   * Footer actions for the whole view, so the Status drop-up is present on every tab rather
   * than only the one that happens to host a panel. Mirrors the quote and purchase-order
   * views: a single TabAction carrying child items renders as a drop-up.
   */
  private buildActionMenu(): void {
    // The container's job can be null mid-teardown - dispose() nulls it while an in-flight save's
    // afterSave still drives a re-render - so read it through the nullable backing field and bail
    // rather than trip the throwing fabricationJob getter inside render.
    const job = this.containerManager.container.fabricationJob;
    if (!job) return;

    const actions: TabAction[] = [];
    const state = job.state;

    const status: TabAction = {
      caption: () => LangFabricationJobActions.statusButton(),
      items: getAvailableJobTransitions(state).map(target => ({
        caption: () => getFabricationJobStateLabel(target),
        onClick: async () => await this.transitionTo(target)
      }))
    };

    // Draft and Completed have no legal next state, and a lock holder must not drive the
    // lifecycle from a read-only view - in both cases the button is absent rather than empty.
    if ((status.items?.length ?? 0) > 0 && !this.containerManager.isLockedFromUse()) actions.push(status);

    // Backing field, not the tabManager getter: the getter throws rather than returning null when
    // the control has not been created yet, or after dispose() has torn it down.
    this._tabManager?.setActions(actions);
  }

  /**
   * Close the footer Status menu. The dropdown only self-closes on its panel's
   * webmodule-select event, and the footer wires its menu items with a plain click, so nothing
   * closes it for us. Left open it would silently repopulate with the NEXT state's options when
   * the action list is rebuilt on the following render, which reads as the menu never closing.
   */
  private closeFooterActionMenu(): void {
    // This view's own tab manager only - not every wm-tab-manager on the page, which would reach
    // into an unrelated screen's open menu.
    const manager = this.tabManager;
    const roots: ParentNode[] = manager.shadowRoot ? [manager, manager.shadowRoot] : [manager];
    roots.forEach(root =>
      root
        .querySelectorAll<HTMLElement & { hide?: () => void }>('webmodule-dropdown[open]')
        .forEach(dropdown => dropdown.hide?.())
    );
  }

  /**
   * Footer twin of the job list's Add to Purchase Order, down to offering the move to Awaiting
   * Order afterwards, so the same job reached from either screen behaves the same way.
   */
  private async addToPurchaseOrder(): Promise<void> {
    this.closeFooterActionMenu();

    const job = this.containerManager.fabricationJob;
    if (!(await addJobToPurchaseOrder(job.branchId, job.id))) return;
    await offerAdvanceToAwaitingOrder(job);

    // That offer transitions through the API directly, so the manager's copy of the job is behind
    // on both state and recordVersion. Reload before redrawing, or the Status menu keeps offering
    // the old state's transitions and the next one fails on a stale record version.
    await this.containerManager.loadFromServer();
    await this.refreshAll();
  }

  private async transitionTo(target: FabricationJobState): Promise<void> {
    this.closeFooterActionMenu();

    // Awaiting Order means "waiting on the order", so reaching it with no purchase order is worth
    // pointing out. Its own prompt replaces the generic confirmation below - it already offers
    // Cancel, and two dialogs back to back to make one decision is one too many. Fabricator mode
    // only: outside it poBadges is never loaded (loadPoBadges bails) and the prompt's assign path
    // is a fabricator-only affordance, so gate it the same way purchaseOrdersTemplate does.
    if (target === FabricationJobState.AwaitingOrder && userDataStore.isFabricatorMode && this.poBadges.length === 0) {
      const choice = await NoPurchaseOrderDialog.Execute();
      if (choice === null) return;
      if (choice === 'assign') {
        const job = this.containerManager.fabricationJob;
        if (!(await addJobToPurchaseOrder(job.branchId, job.id))) return;
      }
      await this.applyTransition(target);
      return;
    }

    // Every other state change is confirmed. The two that fan out to quote items say how many
    // items they will change; the rest get the generic prompt, so no status ever changes on a
    // single stray click.
    const message = transitionAffectsQuoteItems(target)
      ? target === FabricationJobState.Completed
        ? LangFabricationJobActions.confirmCompleted(this.containerManager.items.length)
        : LangFabricationJobActions.confirmInProduction(this.containerManager.items.length)
      : LangFabricationJobActions.confirmTransition(getFabricationJobStateLabel(target));
    const confirmed = await AskConfirmation(message, getConfirmationButtons()[ConfirmationButtonType.yesNo]);
    if (!confirmed) return;
    await this.applyTransition(target);
  }

  private async applyTransition(target: FabricationJobState): Promise<void> {
    const ok = await this.containerManager.transitionState(target);
    if (!ok) return;
    fireQuickSuccessToast(LangFabricationJobActions.transitionedToast(getFabricationJobStateLabel(target)));
    await this.refreshAll();
  }

  // A job is linked to POs only once it leaves Draft; don't fetch for Draft jobs. Fabricator mode only.
  private async loadPoBadges(): Promise<void> {
    const job = this.containerManager.fabricationJob;
    if (!userDataStore.isFabricatorMode || job.state === FabricationJobState.Draft) return;
    const badges = await getApiFactory()
      .fabricationPurchaseOrder()
      .getPurchaseOrdersForJob({ fabricationJobId: job.id });
    this.poBadges = badges ?? [];
  }

  getTitle(): Snippet {
    const job = this.containerManager.fabricationJob;
    return html`${LangFabricationJobDetailView.jobTitleHeading(
      job.jobNumber.toString(),
      job.title || LangFabricationJobDetailView.untitled()
    )}
    ${getFabricationJobStatus(job.state, true)}`;
  }

  // Why only the lock, not Draft? Because the author can be reassigned in any state. Title and
  // description still follow the Draft rule: their editors render read-only outside Draft, and
  // the data tracker never copies a read-only editor back onto the job.
  public isDataReadonly(): boolean {
    return this.containerManager.isLockedFromUse();
  }

  public internalDataChanged(): boolean {
    return !this.abandoned && this.containerManager.jobChanged();
  }

  public prepareForSave(): Promise<void> {
    if (!this.isDataReadonly() && this.dataTracker.modified) this.dataTracker.applyChangeToValue();
    return Promise.resolve();
  }

  protected async internalSaveData(): Promise<boolean> {
    const result = await this.containerManager.updateJob();
    return result !== null;
  }

  async setActiveTabByHash(): Promise<void> {
    // Handle quote-item-{quoteItemId} hash — navigate to Job Items tab and open the item
    const hash = window.location.hash.replace('#', '');
    if (hash.startsWith('quote-item-')) {
      const quoteItemId = hash.substring('quote-item-'.length);
      const jobItem = this.containerManager.items.find(i => i.quoteItemId === quoteItemId);
      if (jobItem) {
        // Switch to items tab first
        const itemsPage = this.tabManager.pages.find(p => p.fragment === 'items');
        if (itemsPage) await this.tabManager.setActivePage(itemsPage);
        // Open the item tab using a minimal row — openQuoteItemTab loads quote data itself
        await this.openQuoteItemTab({ fabricationJobItem: jobItem });
        return;
      }
    }
    await super.setActiveTabByHash();
  }

  protected createTabManager(): TabManager {
    return new TabManager({
      initialIndex: 0,
      pages: () => [this.createInformationTab(), this.createJobItemsTab()]
    });
  }

  private createInformationTab(): TabPage {
    return {
      caption: () => LangFabricationJobDetailView.informationTab(),
      // Footer start region, so the job's orders sit on the footer line rather than above the
      // form. Carried by both main pages because the region is per-page but the orders are a
      // fact about the job, like the Status button opposite them.
      secondaryButtons: () => this.purchaseOrdersTemplate(),
      canLeave: async () => await this.allowPageSwitch(),
      content: () => this.informationTemplate(),
      fragment: 'information'
    };
  }

  private createJobItemsTab(): TabPage {
    return {
      caption: () => `${LangFabricationJobDetailView.jobItemsTab()} (${this.containerManager.items.length})`,
      secondaryButtons: () => this.purchaseOrdersTemplate(),
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        if (!this.itemsTable) {
          this.itemsTable = await constructAsync(
            new FabricationJobItemsTable({
              containerManager: this.containerManager,
              isReadonly: () => this.containerManager.isReadOnly(),
              onItemClick: async (row: JobItemRow) => this.openQuoteItemTab(row),
              onRecordDelivery: async (row: JobItemRow) => this.recordDelivery(row),
              onAmendDelivery: async (row: JobItemRow) => this.viewDelivery(row)
            })
          );
        } else {
          // Re-render the table to pick up any in-memory changes (e.g. unitOverrides
          // updated after editing a job item's V6 options).
          this.itemsTable.refreshData();
        }
        if (!this.optimizationPanel) {
          this.optimizationPanel = await constructAsync(
            new OptimizationPanel({ containerManager: this.containerManager })
          );
        } else {
          this.optimizationPanel.refresh();
        }
      },
      content: () => {
        const itemCount = this.containerManager.items.length;
        return html`
          <div>
            <div class="page-configuration-container">
              <h2 class="page-configuration-title">${LangFabricationJobDetailView.jobItemsTab()} (${itemCount})</h2>
              <webmodule-icon-button
                library="fa"
                name="fas-arrows-rotate"
                label=${LangFabricationJobDetailView.refreshButton()}
                title=${LangFabricationJobDetailView.refreshButton()}
                @click=${() => this.refreshJobItems()}
              ></webmodule-icon-button>
            </div>
            ${this.itemsTable?.ui ?? html`<div>${LangFabricationJobDetailView.loadingText()}</div>`}
            ${this.optimizationPanel?.ui ?? html``}
          </div>
        `;
      },
      fragment: 'items'
    };
  }

  public async refreshItemsTable(): Promise<void> {
    if (this.itemsTable) {
      await this.itemsTable.refreshData();
    }
    this.requestUpdate();
  }

  public refreshOptimizationPanel(): void {
    this.optimizationPanel?.refresh();
    this.requestUpdate();
  }

  /**
   * Refresh every panel this view owns. Callers say "something changed" rather than naming
   * panels, so adding a panel is a one-line change here instead of a hunt through the page
   * and the lifecycle panel's own callback.
   */
  public async refreshAll(): Promise<void> {
    await this.refreshItemsTable();
    this.refreshOptimizationPanel();
    // Purchase-order links change under this view in two ways: the no-purchase-order prompt can
    // add the job to one, and leaving Draft makes them loadable at all (loadPoBadges skips Draft).
    // Without this the PO section keeps showing whatever was true when the page opened.
    await this.loadPoBadges();
  }

  private async refreshJobItems(): Promise<void> {
    await this.containerManager.loadFromServer();
    await this.refreshItemsTable();
  }

  /**
   * The one path that records a delivery: prompt with the job's Ready-for-Delivery items, submit
   * the ticked ones, report, refresh. Viewing an already-recorded delivery is read-only and opens
   * the dialog directly, so it does not pass through here.
   */
  private async runDeliveryDialog(
    quoteItemIds: string[],
    initial: DeliveryEntry,
    candidates: DeliveryCandidate[] = []
  ): Promise<void> {
    if (quoteItemIds.length === 0) return;
    const entered = await RecordDeliveryDialog.Execute(initial, quoteItemIds, candidates);
    if (!entered || entered.quoteItemIds.length === 0) return;
    const ok = await this.containerManager.recordDelivery(entered.quoteItemIds, entered.deliveryDate, entered.notes);
    if (!ok) return;
    fireQuickSuccessToast(LangFabricationJobActions.deliveryRecordedToast(entered.quoteItemIds.length));
    await this.refreshItemsTable();
  }

  /** Clicking a Ready-for-Delivery badge: pick which of the job's ready items are going out. */
  private async recordDelivery(row: JobItemRow): Promise<void> {
    await this.runDeliveryDialog(
      [row.fabricationJobItem.quoteItemId],
      // FieldType.date bindings hold the server value; the tracker converts to local for the input.
      // Pass a server-format "now" and let it do the conversion rather than pre-converting here.
      { deliveryDate: nowServer(), notes: '' },
      this.itemsTable?.deliveryCandidates() ?? []
    );
  }

  /**
   * Clicking a Delivered badge: show that delivery, read-only, listing every item of the same
   * batch. Recorded deliveries are not editable - that was never a requirement.
   */
  private async viewDelivery(row: JobItemRow): Promise<void> {
    const quoteItemId = row.fabricationJobItem.quoteItemId;
    const fab = this.containerManager.itemFabrication(quoteItemId);
    if (!fab?.deliveredDate) return;
    const batchId = fab.deliveryBatchId;
    await RecordDeliveryDialog.View(
      // Raw server value: the tracker's FieldType.date binding converts it to local for display.
      { deliveryDate: fab.deliveredDate, notes: fab.deliveryNotes ?? '' },
      this.itemsTable?.deliveredCandidates(batchId, quoteItemId) ?? []
    );
  }

  /**
   * The job's purchase orders in the footer's start region, with the Add To badge after them -
   * the same affordance the job list uses, so adding an order looks the same from either screen.
   * Renders nothing at all when there is neither an order to show nor the right to add one.
   */
  private purchaseOrdersTemplate(): TemplateResult {
    const canAdd =
      canJobJoinPurchaseOrder(this.containerManager.fabricationJob.state) &&
      userDataStore.isFabricatorMode &&
      !this.containerManager.isLockedFromUse();
    if (this.poBadges.length === 0 && !canAdd) return html``;

    return html`<div class="fabrication-job-po-badges">
      <span>${LangFabricationPurchaseOrder.purchaseOrdersLabel()}</span>
      ${this.poBadges.map(po => getPurchaseOrderBadgeLink(po))}
      ${canAdd ? getAddToPurchaseOrderBadge(() => void this.addToPurchaseOrder()) : html``}
    </div>`;
  }

  private informationTemplate(): TemplateResult {
    const forms = new FormInputAssistant(this.dataTracker, this.containerManager.isReadOnly());
    const authorForms = new FormInputAssistant(this.dataTracker, this.isDataReadonly());
    const users = this.branchUserCache.getBranchUsers(this.containerManager.fabricationJob.branchId);
    const userToOption = (u: TenantLoginPublicInfo) => ({ text: u.friendlyName ?? '', value: u.id, disabled: false });

    return html`
      <form class="py-3 px-0 form-two-col">
        <div class="row">
          <div>
            ${forms.text('title', LangFabricationJobDetailView.titleLabel(), 200)}
            ${forms.note('description', LangFabricationJobDetailView.descriptionLabel(), 3000)}
            ${authorForms.arraySelect('assignedToUserId', users, userToOption, {
              title: LangFabricationJobDetailView.authorLabel()
            })}
            ${forms.dateReadonly('dateCreated', LangFabricationJobDetailView.createdLabel())}
            ${forms.dateReadonly('lastModifiedDate', LangFabricationJobDetailView.modifiedLabel())}
          </div>
        </div>
      </form>
    `;
  }

  private async openQuoteItemTab(row: JobItemRow): Promise<void> {
    // Focus existing tab if already open
    const fabItemId = row.fabricationJobItem.id;
    const existingPage = this.tabManager.pages.find(p => p.fragment === `fab-item-${fabItemId}`);
    if (existingPage) {
      await this.tabManager.setActivePage(existingPage);
      return;
    }

    // Resolve quote item — may come pre-loaded from the items table, or we need to look it up
    let qi = row.quoteItem;
    if (!qi) {
      // Look up the quote item via the quote numbers endpoint to get the quoteId,
      // then load the quote to find the item
      const quoteNumberResult = await getApiFactory()
        .quote()
        .getQuoteNumbersByQuoteItemIds({
          quoteItemIds: [row.fabricationJobItem.quoteItemId]
        });
      const mapping = quoteNumberResult?.items?.[0];
      if (!mapping) return;
      const qm = getQuoteContainerManager(mapping.quoteId);
      qm.forceLockout = true;
      await qm.needsQuote();
      await qm.needsQuoteItems();
      this.quoteManagerCache.set(mapping.quoteId, qm);
      qi = qm.quoteItemContainer(row.fabricationJobItem.quoteItemId)?.item;
      if (!qi) return;
    }

    // Load/cache QuoteContainerManager for this quote
    const quoteId = qi.quoteId;
    let qm = this.quoteManagerCache.get(quoteId);
    if (!qm) {
      qm = getQuoteContainerManager(quoteId);
      qm.forceLockout = true;
      await qm.needsQuote();
      await qm.needsQuoteItems();
      this.quoteManagerCache.set(quoteId, qm);
    }

    const container = qm.quoteItemContainer(qi.id);
    const quoteItemViewOptions: QuoteItemViewOptions = {
      supplierId: qm.quote.supplierId,
      quoteManager: qm,
      quoteItemContainer: container,
      isClientFacing: () => false
    };

    // Create the V6 frame view with overrides, or a standard view for non-frame items
    let quoteItemView: QuoteItemView | null = null;
    const providerType = container.item.serviceProvider;
    const contentType = container.item.quoteItemContentType;
    if (providerType !== '' && qm.isV6 && contentType === supplierQuoteItemContentType.CID_FRAM) {
      quoteItemView = await constructAsync(
        new FabricationJobItemFrameView({
          quoteItemViewOptions,
          fabricationJobItem: row.fabricationJobItem,
          fabricationJobManager: this.containerManager,
          quoteManager: qm,
          quoteItemContainer: container,
          supplierId: qm.quote.supplierId
        })
      );
    } else {
      quoteItemView = await constructAsync(new QuoteItemView(quoteItemViewOptions));
    }
    if (!quoteItemView) return;

    try {
      await quoteItemView.prepareEditor();
      if (!quoteItemView.readyToEdit) return;
    } catch {
      return;
    }

    if (!quoteItemView.isTab()) return;
    const page = quoteItemView.createTabPage();
    page.fragment = `fab-item-${fabItemId}`;
    this.tabManager.addPage(page);
  }

  // Triggers the caller-supplied delete handler (see FabricationJobViewOptions.onDelete).
  // `protected` so it can be wired to a delete action by hosts/subclasses.
  protected async deleteJob(): Promise<void> {
    if (this.onDelete) {
      await this.onDelete();
    }
  }
}

@customElement('wm-fabricationjobview')
export class FabricationJobView extends ModalViewBase {
  view: FabricationJobDataEntryView | null = null;
  private options: FabricationJobViewOptions;

  constructor(options: FabricationJobViewOptions) {
    super();
    this.options = options;
  }

  protected get modalSize() {
    return 'modal-fullscreen';
  }

  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(new FabricationJobDataEntryView(this.options, this));
  }

  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  protected bodyTemplate(): TemplateResult {
    return html`${this.view?.ui}`;
  }
}
